import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError, fmtINR, downloadCsv } from "@/lib/api";
import { useOutletContext } from "react-router-dom";
import { PageHeader, StatusBadge } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { useState } from "react";
import { Loader2, Mail, MapPin, Download, Check, X, UserCog, FolderLock, FileText, Eye, History, Trash2, Pencil, KeyRound, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { LoginActivity } from "@/components/LoginActivity";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const AVATAR_TINTS = [
  "bg-blue-100 text-blue-700", "bg-emerald-100 text-emerald-700",
  "bg-violet-100 text-violet-700", "bg-amber-100 text-amber-700", "bg-rose-100 text-rose-700",
];

export default function Directory() {
  const { role } = useOutletContext();
  const qc = useQueryClient();
  const { data: employees = [], isLoading } = useQuery({
    queryKey: ["employees"], queryFn: async () => (await api.get("/employees")).data,
  });
  const { data: profileReqs = [] } = useQuery({
    queryKey: ["profile-requests"], queryFn: async () => (await api.get("/profile/requests")).data,
    enabled: role === "hr",
  });
  const decideMut = useMutation({
    mutationFn: async ({ id, status }) => (await api.patch(`/profile/requests/${id}`, { status })).data,
    onSuccess: (_, v) => { qc.invalidateQueries({ queryKey: ["profile-requests"] }); qc.invalidateQueries({ queryKey: ["employees"] }); toast.success(`Profile update ${v.status.toLowerCase()}`); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const [docsFor, setDocsFor] = useState(null);
  const [editFor, setEditFor] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [activityFor, setActivityFor] = useState(null);
  const editMut = useMutation({
    mutationFn: async () => (await api.patch(`/employees/${editFor.id}`, editForm)).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["employees"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); setEditFor(null); toast.success(d?.revision_emailed ? "Employee updated — salary revision letter emailed" : d?.email_changed ? "Employee updated — new login email notified" : "Employee updated"); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/employees/${id}`)).data,
    onSuccess: (d) => { qc.invalidateQueries(); toast.success(`${d.name} (${d.deleted}) permanently deleted`); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const resetMut = useMutation({
    mutationFn: async (id) => (await api.post(`/employees/${id}/send-reset`)).data,
    onSuccess: (d) => toast.success(`Password reset link sent to ${d.email}`),
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const [selected, setSelected] = useState(new Set());
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulkDeleteMut = useMutation({
    mutationFn: async (ids) => { for (const id of ids) { await api.delete(`/employees/${id}`); } return ids.length; },
    onSuccess: (n) => { qc.invalidateQueries(); setSelected(new Set()); toast.success(`${n} employee(s) permanently deleted`); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const openEdit = (e) => { setEditForm({ employee_id: e.employee_id, email: e.email || "", dob: e.dob || "", doj: e.doj || "", designation: e.designation, department: e.department, location: e.location || "", shift_id: e.shift_id || "", annual_ctc: e.annual_ctc || 0 }); setEditFor(e); };
  const { data: docs = [], isLoading: docsLoading, isError: docsError } = useQuery({
    queryKey: ["emp-documents", docsFor?.email],
    queryFn: async () => (await api.get("/documents", { params: { email: docsFor.email } })).data,
    enabled: !!docsFor,
  });
  const openDoc = async (id) => {
    try {
      const res = await api.get(`/documents/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Could not open document");
    }
  };
  const { data: demoCfg } = useQuery({ queryKey: ["public-config"], queryFn: async () => (await api.get("/public/config")).data, enabled: role === "hr" });
  const purgeMut = useMutation({
    mutationFn: async () => (await api.delete("/admin/demo-data")).data,
    onSuccess: () => { qc.invalidateQueries(); toast.success("Demo data removed. Only real staff remain."); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const verifyMut = useMutation({
    mutationFn: async ({ id, status }) => (await api.patch(`/documents/${id}/verify`, { status })).data,
    onSuccess: (_, v) => { qc.invalidateQueries({ queryKey: ["emp-documents"] }); toast.success(`Document ${v.status.toLowerCase()}`); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const { data: auditLog = [] } = useQuery({
    queryKey: ["profile-audit"], queryFn: async () => (await api.get("/profile/audit")).data,
    enabled: role === "hr",
  });
  const { data: deleteAudit = [] } = useQuery({
    queryKey: ["delete-audit"], queryFn: async () => (await api.get("/admin/delete-audit")).data,
    enabled: role === "hr",
  });
  const { data: shifts = [] } = useQuery({
    queryKey: ["shifts"], queryFn: async () => (await api.get("/shifts")).data,
    enabled: role === "hr",
  });
  const pending = profileReqs.filter((r) => r.status === "Pending");
  const activeEmployees = employees.filter((e) => e.status !== "Relieved");
  const inactiveEmployees = employees.filter((e) => e.status === "Relieved");
  const FIELD_LABELS = {
    phone: "Phone", location: "Location", emergency_contact_name: "Emergency Name",
    emergency_contact_phone: "Emergency Phone", bank_account_no: "Bank A/C", bank_ifsc: "IFSC",
  };

  return (
    <div>
      <PageHeader title="Employee Directory" subtitle="Active workforce with statutory & profile details.">
        {role === "hr" && (
          <>
            {demoCfg?.demo_data && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="rounded-lg text-destructive border-destructive/40 hover:bg-destructive/10" data-testid="purge-demo-button">
                    <Trash2 className="w-4 h-4 mr-1" /> Remove demo data
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="purge-demo-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove all seeded demo data?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This permanently deletes the 5 demo employees (Rahul, Ananya, Vikram, Sneha, Arjun), their logins, attendance, leaves, payslips, documents, and the 5 demo candidates/offers. Real staff and your HR account are untouched. This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="purge-demo-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => purgeMut.mutate()} className="bg-destructive hover:bg-destructive/90" data-testid="purge-demo-confirm">
                      Yes, remove demo data
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <Button variant="outline" size="sm" className="rounded-lg" onClick={() => downloadCsv("/export/employees.csv", "skanovix_employees.csv")} data-testid="directory-export-button">
              <Download className="w-4 h-4 mr-1" /> CSV
            </Button>
          </>
        )}
      </PageHeader>

      {role === "hr" && pending.length > 0 && (
        <Card className="border border-amber-200 dark:border-amber-800 rounded-xl overflow-hidden mb-6" data-testid="profile-approvals">
          <div className="px-5 py-4 border-b border-border flex items-center gap-2 bg-amber-50/60 dark:bg-amber-950/20">
            <UserCog className="w-4 h-4 text-amber-600" />
            <h3 className="font-heading font-semibold">Profile Update Requests</h3>
            <span className="text-xs font-mono bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded-full ml-1">{pending.length}</span>
          </div>
          <div className="divide-y divide-border">
            {pending.map((r) => (
              <div key={r.id} className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-3.5" data-testid={`profile-request-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">{r.employee_name} <span className="font-mono text-xs text-muted-foreground">({r.employee_id})</span></p>
                  <p className="text-xs text-muted-foreground">
                    {Object.entries(r.changes).map(([k, v]) => `${FIELD_LABELS[k] || k}: ${v}`).join(" • ")}
                  </p>
                </div>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="secondary" className="h-8 rounded-lg" onClick={() => decideMut.mutate({ id: r.id, status: "Approved" })} disabled={decideMut.isPending} data-testid={`approve-profile-${r.id}`}><Check className="w-4 h-4" /></Button>
                  <Button size="sm" variant="outline" className="h-8 rounded-lg" onClick={() => decideMut.mutate({ id: r.id, status: "Rejected" })} disabled={decideMut.isPending} data-testid={`reject-profile-${r.id}`}><X className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 mb-8">
            {activeEmployees.map((e, i) => (
              <Card key={e.id} className="p-5 border border-border rounded-xl hover:shadow-md transition-shadow" data-testid={`employee-card-${e.employee_id}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold ${AVATAR_TINTS[i % AVATAR_TINTS.length]}`}>
                    {e.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{e.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{e.designation}</p>
                  </div>
                </div>
                <div className="mt-4 space-y-1.5 text-xs text-muted-foreground">
                  <p className="flex items-center gap-1.5 truncate"><Mail className="w-3 h-3" /> {e.email}</p>
                  <p className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {e.location} • {e.department}</p>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <span className="font-mono text-xs text-accent font-semibold">{fmtINR(e.annual_ctc)}/yr</span>
                  <StatusBadge status={e.status} />
                </div>
                <p className="mt-2 text-[10px] font-mono text-muted-foreground">{e.employee_id} • UAN {e.uan}</p>
              </Card>
            ))}
          </div>

          <Card className="border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3">
              <h3 className="font-heading font-semibold">Payroll Roster</h3>
              {role === "hr" && (
                <div className="flex items-center gap-2">
                  {inactiveEmployees.length > 0 && (
                    <Button size="sm" variant="outline" className="rounded-lg" onClick={() => setSelected(new Set(inactiveEmployees.map((e) => e.id)))} data-testid="select-inactive-button">
                      Select exited ({inactiveEmployees.length})
                    </Button>
                  )}
                  {selected.size > 0 && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" className="rounded-lg text-destructive border-destructive/40 hover:bg-destructive/10" data-testid="bulk-delete-button">
                          <Trash2 className="w-4 h-4 mr-1" /> Delete selected ({selected.size})
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent data-testid="bulk-delete-dialog">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete {selected.size} employee(s)?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This permanently deletes the selected employees and all their records (login, attendance, leaves, payslips, documents, relieving, profile). This cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="bulk-delete-cancel">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => bulkDeleteMut.mutate([...selected])} disabled={bulkDeleteMut.isPending} className="bg-destructive hover:bg-destructive/90" data-testid="bulk-delete-confirm">
                            Yes, delete permanently
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              )}
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {role === "hr" && <TableHead className="w-8"></TableHead>}
                    <TableHead>ID</TableHead><TableHead>Name</TableHead><TableHead>Designation</TableHead>
                    <TableHead className="text-right">Annual CTC</TableHead>
                    <TableHead className="text-right">Net / Month</TableHead>
                    <TableHead>Status</TableHead>
                    {role === "hr" && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employees.map((e) => (
                    <TableRow key={e.id} data-testid={`employee-row-${e.employee_id}`} className={e.status === "Relieved" ? "bg-destructive/5" : ""}>
                      {role === "hr" && <TableCell><Checkbox checked={selected.has(e.id)} onCheckedChange={() => toggleSel(e.id)} data-testid={`select-emp-${e.employee_id}`} /></TableCell>}
                      <TableCell className="font-mono text-xs">{e.employee_id}</TableCell>
                      <TableCell className="font-medium">{e.name}</TableCell>
                      <TableCell className="text-muted-foreground">{e.designation}</TableCell>
                      <TableCell className="text-right font-mono">{fmtINR(e.annual_ctc)}</TableCell>
                      <TableCell className="text-right font-mono">{fmtINR(e.breakdown?.net_monthly || 0)}</TableCell>
                      <TableCell><StatusBadge status={e.status} /></TableCell>
                      {role === "hr" && (
                        <TableCell className="text-right whitespace-nowrap">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(e)} data-testid={`edit-button-${e.employee_id}`} title="Edit ID / details"><Pencil className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => setActivityFor(e)} data-testid={`activity-button-${e.employee_id}`} title="Sign-in activity"><KeyRound className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => setDocsFor(e)} data-testid={`docs-button-${e.employee_id}`} title="View documents"><FolderLock className="w-4 h-4" /></Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="ghost" data-testid={`reset-button-${e.employee_id}`} title="Send password reset link"><RotateCcw className="w-4 h-4" /></Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent data-testid={`reset-dialog-${e.employee_id}`}>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Send password reset link?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  A secure, single-use reset link (valid for 1 hour) will be emailed to {e.name} at {e.email}. Their current password stays active until they set a new one.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel data-testid={`reset-cancel-${e.employee_id}`}>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => resetMut.mutate(e.id)} data-testid={`reset-confirm-${e.employee_id}`}>
                                  Send reset link
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10" data-testid={`delete-button-${e.employee_id}`} title="Delete employee"><Trash2 className="w-4 h-4" /></Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent data-testid={`delete-dialog-${e.employee_id}`}>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete {e.name}?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This permanently deletes {e.name} ({e.employee_id}) and their login, attendance, leaves, payslips, documents, relieving and profile records. This cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel data-testid={`delete-cancel-${e.employee_id}`}>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => deleteMut.mutate(e.id)} className="bg-destructive hover:bg-destructive/90" data-testid={`delete-confirm-${e.employee_id}`}>
                                  Yes, delete permanently
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>

          {role === "hr" && auditLog.length > 0 && (
            <Card className="border border-border rounded-xl overflow-hidden mt-6" data-testid="profile-audit-log">
              <div className="px-5 py-4 border-b border-border flex items-center gap-2"><History className="w-4 h-4 text-accent" /><h3 className="font-heading font-semibold">Profile Change Audit Log</h3></div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Employee</TableHead><TableHead>Approved Changes</TableHead><TableHead>Approved By</TableHead><TableHead>When</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {auditLog.map((a) => (
                      <TableRow key={a.id} data-testid={`audit-row-${a.id}`}>
                        <TableCell className="font-medium">{a.employee_name}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[280px] truncate">{Object.entries(a.changes).map(([k, v]) => `${k}: ${v}`).join(" • ")}</TableCell>
                        <TableCell className="text-xs">{a.approved_by}</TableCell>
                        <TableCell className="font-mono text-xs">{new Date(a.approved_at).toLocaleString("en-IN")}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}
          {role === "hr" && deleteAudit.length > 0 && (
            <Card className="border border-border rounded-xl overflow-hidden mt-6" data-testid="delete-audit-log">
              <div className="px-5 py-4 border-b border-border flex items-center gap-2"><Trash2 className="w-4 h-4 text-destructive" /><h3 className="font-heading font-semibold">Deleted Employees Log</h3></div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Employee</TableHead><TableHead>ID</TableHead><TableHead>Deleted By</TableHead><TableHead>When</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {deleteAudit.map((a) => (
                      <TableRow key={a.id} data-testid={`delete-audit-row-${a.id}`}>
                        <TableCell className="font-medium">{a.name}</TableCell>
                        <TableCell className="font-mono text-xs">{a.employee_id}</TableCell>
                        <TableCell className="text-xs">{a.deleted_by}</TableCell>
                        <TableCell className="font-mono text-xs">{new Date(a.at).toLocaleString("en-IN")}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}
        </>
      )}

      {/* HR edit employee */}
      <Dialog open={!!editFor} onOpenChange={(o) => !o && setEditFor(null)}>
        <DialogContent className="max-w-md" data-testid="edit-employee-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Pencil className="w-4 h-4 text-accent" /> Edit Employee</DialogTitle>
            <DialogDescription>{editFor?.name} — changing the Employee ID updates payslips, leaves and documents too.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="space-y-1.5 col-span-2"><Label>Employee ID</Label><Input value={editForm.employee_id || ""} onChange={(e) => setEditForm({ ...editForm, employee_id: e.target.value.toUpperCase() })} className="font-mono" data-testid="edit-employee-id-input" /></div>
            <div className="space-y-1.5 col-span-2"><Label>Login / Official Email</Label><Input type="email" value={editForm.email || ""} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="font-mono" data-testid="edit-email-input" /><p className="text-[11px] text-muted-foreground">Changing this updates the sign-in email across all records; the employee is notified at the new address.</p></div>
            <div className="space-y-1.5"><Label>Date of Birth</Label><Input type="date" value={editForm.dob || ""} onChange={(e) => setEditForm({ ...editForm, dob: e.target.value })} data-testid="edit-dob-input" /></div>
            <div className="space-y-1.5"><Label>Date of Joining</Label><Input type="date" value={editForm.doj || ""} onChange={(e) => setEditForm({ ...editForm, doj: e.target.value })} data-testid="edit-doj-input" /></div>
            <div className="space-y-1.5"><Label>Location</Label><Input value={editForm.location || ""} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} data-testid="edit-location-input" /></div>
            <div className="space-y-1.5"><Label>Designation</Label><Input value={editForm.designation || ""} onChange={(e) => setEditForm({ ...editForm, designation: e.target.value })} data-testid="edit-designation-input" /></div>
            <div className="space-y-1.5"><Label>Department</Label><Input value={editForm.department || ""} onChange={(e) => setEditForm({ ...editForm, department: e.target.value })} data-testid="edit-department-input" /></div>
            <div className="space-y-1.5 col-span-2"><Label>Annual CTC (₹)</Label><Input type="number" min="1" value={editForm.annual_ctc ?? ""} onChange={(e) => setEditForm({ ...editForm, annual_ctc: Number(e.target.value) })} className="font-mono" data-testid="edit-ctc-input" /><p className="text-[11px] text-muted-foreground">Updating CTC recomputes the salary breakdown and net pay.</p></div>
            <div className="space-y-1.5 col-span-2"><Label>Assigned Shift</Label>
              <select value={editForm.shift_id || ""} onChange={(e) => setEditForm({ ...editForm, shift_id: e.target.value })} data-testid="edit-shift-select" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="">No shift assigned</option>
                {shifts.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.start_time}–{s.end_time})</option>)}
              </select>
            </div>
          </div>
          <Button onClick={() => editMut.mutate()} disabled={editMut.isPending} className="w-full rounded-lg" data-testid="edit-employee-save-button">
            {editMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
          </Button>
        </DialogContent>
      </Dialog>

      {/* HR sign-in activity */}
      <Dialog open={!!activityFor} onOpenChange={(o) => !o && setActivityFor(null)}>
        <DialogContent className="max-w-md" data-testid="hr-activity-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><KeyRound className="w-4 h-4 text-accent" /> Sign-in Activity</DialogTitle>
            <DialogDescription>{activityFor?.name} — last 20 sign-in attempts.</DialogDescription>
          </DialogHeader>
          {activityFor && <LoginActivity email={activityFor.email} />}
        </DialogContent>
      </Dialog>

      {/* HR employee document viewer */}
      <Dialog open={!!docsFor} onOpenChange={(o) => !o && setDocsFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><FolderLock className="w-4 h-4 text-accent" /> Employee Documents</DialogTitle>
            <DialogDescription>{docsFor?.name} ({docsFor?.employee_id}) — uploaded identity documents.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2" data-testid="hr-docs-dialog">
            {docsLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
            ) : docsError ? (
              <p className="text-sm text-destructive text-center py-8">Could not load documents. Please try again.</p>
            ) : ["PAN", "Aadhaar", "UAN"].map((t) => {
              const d = docs.find((x) => x.doc_type === t);
              return (
                <div key={t} className="flex items-center gap-3 p-3 rounded-lg border border-border" data-testid={`hr-doc-row-${t}`}>
                  <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center"><FileText className="w-4 h-4 text-muted-foreground" /></div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold flex items-center gap-2">{t} {d && <StatusBadge status={d.verification_status || "Pending"} />}</p>
                    <p className="text-xs text-muted-foreground truncate">{d ? d.original_filename : "Not uploaded"}</p>
                  </div>
                  {d && (
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openDoc(d.id)} data-testid={`hr-doc-view-${t}`}><Eye className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => verifyMut.mutate({ id: d.id, status: "Verified" })} disabled={verifyMut.isPending} data-testid={`hr-doc-verify-${t}`} title="Mark verified"><Check className="w-4 h-4 text-emerald-600" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => verifyMut.mutate({ id: d.id, status: "Rejected" })} disabled={verifyMut.isPending} data-testid={`hr-doc-reject-${t}`} title="Reject"><X className="w-4 h-4 text-destructive" /></Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
